import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Table, Typography, Space, Select, Tooltip, Descriptions } from 'antd';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ExpandCollapseAllButton from '../../common/ExpandCollapseAllButton';
import NumberInput from '../../common/NumberInput';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useCanConfigureBoq } from '@/hooks/useCanConfigureBoq';
import { useRoadsAndSewersMarkupFactor } from '../../../hooks/useRoadsAndSewersMarkupFactor';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { flattenBoqSummaryLineItems } from '../../../utils/roadsAndSewersBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from '../../../utils/constants';
import { PRELIMS_MAPPING, LEGACY_METHOD_RELATED_GROUP_IDS } from '../../../utils/prelimsMapping';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import type { ItemLibraryItem } from '../../../api/itemLibrary';
import type { PrelimsItemRecord, DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const { Text, Title } = Typography;

interface BillRow {
  key: string;
  category: string;
  description: string;
  quantity: number | string;
  unit: string;
  rate: number | string;
  amount: number | string;
  code?: string;
  rowType?: 'sub-header' | 'group' | 'item' | 'main-header' | 'column-type-header' | 'line-item';
  children?: BillRow[];
  sectionAmount?: number;
  // Ad-hoc extra rows (useBillSummaryExtraItems.tsx) — see isExtra guard below.
  isExtra?: boolean;
  attachKey?: string;
  nodeType?: 'section' | 'item';
  amountType?: 'number' | 'text';
  amountText?: string;
  dbRate?: string;
}

// Header/section rowTypes across this table — 'sub-header'/'group' are the
// only ones the FIXED data ever produces; 'main-header'/'column-type-header'
// only ever come from ad-hoc extra Section rows (root-level / nested
// respectively — see useBillSummaryExtraItems.tsx's buildExtraRow depth
// convention), but every column below still has to treat all four the same
// way so a newly-added extra section renders consistently.
const isHeaderRowType = (rowType?: string) =>
  rowType === 'sub-header' || rowType === 'group' || rowType === 'main-header' || rowType === 'column-type-header';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const PrelimsBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  // Ad-hoc "Add Item"/"Add Section" extra rows — gated by the "Configure BOQ" switch
  // (next to the Re-calculate button on /roads-sewers/review), independent of Dev
  // Mode, same convention every groundworks Bill Summary follows.
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();
  // Same OH&P/Preliminaries markup factor every other R&S Bill Summary now
  // applies — gated by the roadsAndSewers_includeOhpDisplay/
  // roadsAndSewers_includePreliminariesDisplay checkboxes on ReviewCalculate.tsx,
  // same as useRoadsAndSewersTotalSync's own prelimsTotal. Previously this
  // component ignored those checkboxes and marked up unconditionally, so this
  // Rate column could disagree with Sum Analysis when a toggle was off.
  const markupFactor = useRoadsAndSewersMarkupFactor();
  const { data: itemLibraryData } = useItemLibrary(state.region || '');
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  // SummaryKey modal for ad-hoc extra items — these have no persisted boq-versions
  // entry since they aren't serialised into the exported BOQ payload yet, so this
  // shows the live-derived value instead, same "unsaved" convention
  // DynamicBillSummaryPanel's own SummaryKey column uses.
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<DynamicBillSummaryLineItem | null>(null);

  // Ad-hoc user-added Section/Item rows grafted onto this otherwise-immutable
  // computed table — see useBillSummaryExtraItems.tsx. Gated by
  // dynamicSummaryEnabled, same as every groundworks Bill Summary's own extra
  // items. No `plotMatrixColumns` passed — Roads & Sewers has no Plot Matrix.
  const prelimsExtraItems = state.prelimsExtraItems;
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);
  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: prelimsExtraItems,
    onChange: (updated) => updateField('prelimsExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, ItemLibraryItem>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item);
    });
    return map;
  }, [itemLibraryData]);

  const savedItemsMap = useMemo(() => {
    const map = new Map<string, PrelimsItemRecord>();
    (state.preliminaryItems?.preliminaryItems || []).forEach((saved) => {
      map.set(saved.id, saved);
    });
    return map;
  }, [state.preliminaryItems?.preliminaryItems]);

  // Dev-mode only: pulls the persisted `roadAndSewers.summary.prelims` tree
  // from the same boq-versions API the rest of this review page uses, so the
  // SummaryKey column reflects what was actually saved rather than the live
  // recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );
  const persistedPrelims = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { prelims?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.prelims,
    ),
    [latestBoqVersionData],
  );

  const { processedData, grandTotal } = useMemo(() => {
    const rows: BillRow[] = [];
    let sectionNum = 1;
    let total = 0;

    PRELIMS_MAPPING.forEach((cat) => {
      const catItems: BillRow[] = [];
      let catTotal = 0;
      let itemNum = 1;

      (cat.items ?? []).forEach((item) => {
        const idSuffix = item.description.replace(/[^a-zA-Z0-9]/g, '-');
        const id = `prelim-${cat.id}-${idSuffix}`;
        const legacyGroupId = LEGACY_METHOD_RELATED_GROUP_IDS[item.description];
        const legacyId = legacyGroupId ? `prelim-${cat.id}-${legacyGroupId}-${idSuffix}` : undefined;
        const saved = savedItemsMap.get(id) ?? (legacyId ? savedItemsMap.get(legacyId) : undefined);
        if (!saved || saved.quantity <= 0) return;

        // Variant rows (e.g. "CBR testing option(s)") resolve their code/description from
        // the user's saved selection, same as PreliminaryItems.tsx's buildPrelimRecord.
        const selectedVariant = item.variants?.find((v) => v.code === saved.code);
        const code = item.variants ? (selectedVariant?.code ?? item.code) : item.code;
        const description = selectedVariant?.label ?? item.description;
        const libItem = resourceCodeMap.get((code || '').trim().toUpperCase());
        const libRate = libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
        const baseRate = saved.rate > 0 ? saved.rate : libRate;
        // Rate is rounded to 2dp here — the SAME value shown in the Rate
        // column — so Amount always equals the displayed Rate × displayed
        // Qty exactly. Same convention every groundworks Bill Summary uses.
        const markedUpRate = parseFloat((baseRate * markupFactor).toFixed(2));
        const amount = saved.quantity * markedUpRate;

        catItems.push({
          key: id,
          category: `${sectionNum}.${itemNum}`,
          description,
          quantity: saved.quantity,
          unit: saved.unit,
          rate: markedUpRate,
          amount,
          code: code || undefined,
          rowType: 'item',
        });
        catTotal += amount;
        itemNum++;
      });

      // A category with exactly one named group and no root items carries no
      // real grouping value (matches the fold rule in PreliminaryItems.tsx's
      // groupedTableData) — flatten its items straight into the category
      // instead of showing a redundant single group row.
      const foldSoleGroup = cat.groups.length === 1 && (cat.items?.length ?? 0) === 0;

      cat.groups.forEach((grp) => {
        const buildItemRow = (item: (typeof grp.items)[number], num: string) => {
          const id = `prelim-${cat.id}-${grp.id}-${item.description.replace(/[^a-zA-Z0-9]/g, '-')}`;
          const saved = savedItemsMap.get(id);
          if (!saved || saved.quantity <= 0) return null;

          const selectedVariant = item.variants?.find((v) => v.code === saved.code);
          const code = item.variants ? (selectedVariant?.code ?? item.code) : item.code;
          const description = selectedVariant?.label ?? item.description;
          const libItem = resourceCodeMap.get((code || '').trim().toUpperCase());
          const libRate = libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
          const baseRate = saved.rate > 0 ? saved.rate : libRate;
          // Rate is rounded to 2dp here — the SAME value shown in the Rate
          // column — so Amount always equals the displayed Rate × displayed
          // Qty exactly. Same convention every groundworks Bill Summary uses.
          const markedUpRate = parseFloat((baseRate * markupFactor).toFixed(2));
          const amount = saved.quantity * markedUpRate;

          return {
            row: {
              key: id,
              category: num,
              description,
              quantity: saved.quantity,
              unit: saved.unit,
              rate: markedUpRate,
              amount,
              code: code || undefined,
              rowType: 'item' as const,
            },
            amount,
          };
        };

        if (foldSoleGroup) {
          grp.items.forEach((item) => {
            const built = buildItemRow(item, `${sectionNum}.${itemNum}`);
            if (!built) return;
            catItems.push(built.row);
            catTotal += built.amount;
            itemNum++;
          });
          return;
        }

        const grpItems: BillRow[] = [];
        let grpTotal = 0;
        let grpItemNum = 1;
        grp.items.forEach((item) => {
          const built = buildItemRow(item, `${sectionNum}.${itemNum}.${grpItemNum}`);
          if (!built) return;
          grpItems.push(built.row);
          grpTotal += built.amount;
          grpItemNum++;
        });

        if (grpItems.length > 0) {
          catItems.push({
            key: `grp-${cat.id}-${grp.id}`,
            category: `${sectionNum}.${itemNum}`,
            description: grp.label,
            quantity: '',
            unit: '',
            rate: '',
            amount: '',
            rowType: 'group',
            sectionAmount: grpTotal,
            children: grpItems,
          });
          catTotal += grpTotal;
          itemNum++;
        }
      });

      if (catItems.length > 0) {
        rows.push({
          key: `cat-${cat.id}`,
          category: `${sectionNum}.0`,
          description: cat.label,
          quantity: '',
          unit: '',
          rate: '',
          amount: '',
          rowType: 'sub-header',
          sectionAmount: catTotal,
          children: catItems,
        });
        total += catTotal;
        sectionNum++;
      }
    });

    // Graft ad-hoc extra rows on top of this otherwise-immutable computed
    // tree — nested additions merge onto the matching existing category/group
    // row (by `key`), and root additions become new top-level sections
    // continuing the same whole-number numbering the category rows above
    // use. Same convention every groundworks Bill Summary follows.
    const extraBuckets = prelimsExtraItems ?? {};
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rowsIn: BillRow[]): BillRow[] =>
      rowsIn.map((row) => {
        const bucket = extraBuckets[row.key];
        const existingChildren: BillRow[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(String(c.amount || '0')) || 0), 0),
          }),
        };
      });
    const injectedRows = injectExtraRows(rows);
    let nextRootNumber = sectionNum;
    const rootExtraRows = buildRootExtraRows(extraBuckets['__root__'] ?? [], '__root__', nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    // Orphaned nested buckets (attach-point row no longer exists) wrap under a
    // single synthetic "Additional Items" header instead of landing as bare
    // unlabelled rows — same convention every groundworks Bill Summary follows.
    const orphanedExtraRows = Object.keys(extraBuckets)
      .filter((key) => key !== '__root__' && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: 'Additional Items',
          rowType: 'main-header' as const,
          isExtra: true,
          attachKey: key,
          nodeType: 'section' as const,
          quantity: '',
          unit: '',
          rate: '',
          amount: '',
          dbRate: '',
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || '0') || 0), 0),
          children,
        };
      });
    const finalRows: BillRow[] = [...injectedRows, ...rootExtraRows, ...orphanedExtraRows];

    // Recomputed from the final merged tree (not the pre-injection `total`
    // above) so the footer TOTAL/Sum Analysis always includes extra items too.
    const sumLeaves = (nodes: BillRow[]): number =>
      nodes.reduce((sum, node) => {
        if (node.children && node.children.length > 0) return sum + sumLeaves(node.children);
        const amt = typeof node.amount === 'number' ? node.amount : parseFloat(String(node.amount || '0'));
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0);

    return { processedData: finalRows, grandTotal: sumLeaves(finalRows) };
  }, [savedItemsMap, resourceCodeMap, markupFactor, prelimsExtraItems]);

  // Depth of every row (real category/group nesting AND grafted extra
  // sections alike) in the merged tree above — gates a section row's "Add
  // Section" Actions icon at MAX_SECTION_NESTING_DEPTH.
  const rowDepthMap = useMemo(() => buildSectionDepthMap(processedData), [processedData]);

  // Group (sub-category) rows always show their items — no collapse/expand
  // for them, only the top-level category rows are collapsible.
  const groupRowKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const collect = (rows: BillRow[]) => {
      rows.forEach((row) => {
        if (row.rowType === 'group') keys.push(row.key);
        if (row.children?.length) collect(row.children);
      });
    };
    collect(processedData);
    return keys;
  }, [processedData]);

  const handleExpandCollapseAll = () => {
    if (expandedRowKeys.length > 0) {
      setExpandedRowKeys([]);
    } else {
      const allKeys: React.Key[] = [];
      const collect = (rows: BillRow[]) => {
        rows.forEach((row) => {
          if (row.children?.length) {
            allKeys.push(row.key);
            collect(row.children);
          }
        });
      };
      collect(processedData);
      setExpandedRowKeys(allKeys);
    }
  };

  const fmtCurrency = (v: number) =>
    `£${v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const columns = [
    {
      title: 'Item',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      align: 'center' as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType))
          return <Text strong>{value}</Text>;
        return value;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, record: BillRow) => {
        if (record.rowType === 'sub-header' || record.rowType === 'main-header')
          return <Text strong className="text-indigo-600">{value}</Text>;
        if (record.rowType === 'group' || record.rowType === 'column-type-header')
          return <Text strong className="text-gray-700">{value}</Text>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Text>{value}</Text>
            {showDevMode && record.code && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2, fontFamily: 'monospace' }}>
                {record.code}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 110,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        // Ad-hoc extra items are directly editable in-cell, same as every
        // groundworks Bill Summary's own Qty column — existing computed rows
        // stay read-only. Text amount type has no Qty at all.
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(String(value)) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey!, record.key, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        if (!value && value !== 0) return '';
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(n) ? '' : <div style={{ textAlign: 'right' }}>{n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}</div>;
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 110,
      align: 'center' as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={value || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey!, record.key, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
            />
          );
        }
        return formatUnit(value);
      },
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 110,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (!value && value !== 0) return '';
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(n) || n === 0) return '';
        return <div style={{ textAlign: 'right' }}>£{n.toFixed(2)}</div>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) {
          const isExpanded = record.rowType === 'group' || expandedRowKeys.includes(record.key);
          if (!isExpanded && record.sectionAmount && record.sectionAmount > 0) {
            return (
              <div style={{ textAlign: 'right' }}>
                <span className="font-semibold">
                  {fmtCurrency(record.sectionAmount)}
                </span>
              </div>
            );
          }
          return '';
        }
        // Text amount type (extra items only) — a free-typed value like
        // "Included"/"Rate Only" instead of a computed £ amount.
        if (record.amountType === 'text') {
          return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
        }
        if (!value && value !== 0) return '';
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        if (isNaN(n) || n === 0) return '';
        return <div style={{ textAlign: 'right' }}>{fmtCurrency(n)}</div>;
      },
    },
    // ── Dev-mode only: persisted-vs-live comparison column ───────────────────
    ...(showDevMode ? [{
      title: 'SummaryKey',
      key: 'summaryKey',
      width: 120,
      align: 'right' as const,
      render: (_value: unknown, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (record.isExtra) {
          const node = findExtraNode(prelimsExtraItems?.[record.attachKey!] ?? [], record.key) as DynamicBillSummaryItemNode | null;
          if (!node) return '';
          const liveAmount = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount || '0')) || 0;
          const lineItem: DynamicBillSummaryLineItem = {
            key: node.id,
            description: node.description,
            quantity: node.qty,
            unit: node.unit,
            rate: node.rate,
            amount: liveAmount,
            amountType: node.amountType,
            amountText: node.amountText,
            resourceCode: node.resourceCode,
            resourceCodes: node.resourceCodes,
          };
          const bc = computeDynamicBcCalculation(lineItem, (code) => {
            const libItem = resourceCodeMap.get(code);
            return libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
          });
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 12, color: '#f59e0b' }}>unsaved</Text>
                <InfoCircleOutlined
                  style={{ cursor: 'pointer', color: '#3b82f6' }}
                  onClick={() => setExtraSummaryKeyRecord(lineItem)}
                />
              </div>
              {bc && (
                <Text style={{ fontSize: 10, color: '#3b82f6' }}>
                  BC: {fmtCurrency(bc.amount)}
                </Text>
              )}
            </div>
          );
        }
        const persisted = persistedPrelims.get(record.key);
        if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const liveAmount = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount));
        const mismatch = Math.abs((isNaN(liveAmount) ? 0 : liveAmount) - (persisted.amount || 0)) > 0.01;
        const bcAmount = computeBcCalculation(
          persisted,
          (code) => {
            const libItem = resourceCodeMap.get(code);
            return libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
          },
        ).amount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                {fmtCurrency(persisted.amount || 0)}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(persisted)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: {fmtCurrency(bcAmount)}
            </Text>
          </div>
        );
      },
    }] : []),
    // ── Actions column: Add Section/Add Item on header rows, Edit/Delete on
    // ad-hoc extra rows — gated by dynamicSummaryEnabled, same convention
    // every groundworks Bill Summary follows. ─────────────────────────────
    ...(dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: BillRow) => {
        if (record.isExtra) {
          const node = findExtraNode(prelimsExtraItems?.[record.attachKey!] ?? [], record.key);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                  <Button
                    size="small"
                    icon={<FolderAddOutlined />}
                    title="Add Section"
                    disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH}
                    onClick={() => extraItemsApi.openAddSection(record.attachKey!, record.key)}
                  />
                </Tooltip>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey!, record.key)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey!, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey!, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
            </Space>
          );
        }
        // Existing computed row — immutable, but a header row can host
        // grafted extra rows. A plain item row gets no Actions at all.
        if (!isHeaderRowType(record.rowType)) return '';
        const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
        return (
          <Space size={4}>
            <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
              <Button
                size="small"
                icon={<FolderAddOutlined />}
                title="Add Section"
                disabled={atMaxDepth}
                onClick={() => extraItemsApi.openAddSection(record.key, null)}
              />
            </Tooltip>
            <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
          </Space>
        );
      },
    }] : []),
  ];

  const hasData = processedData.length > 0;

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}>
          Preliminary Items Bill Summary
        </Title>
        <Space>
          {dynamicSummaryEnabled && !state.isViewMode && (
            <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection('__root__', null)}>
              Add Section
            </Button>
          )}
          <ExpandCollapseAllButton expanded={expandedRowKeys.length > 0} onToggle={handleExpandCollapseAll} />
        </Space>
      </div>
      <style>{`
        .prelims-bill-table .ant-table { border-radius: 0 !important; }
        .prelims-bill-table .ant-table-container { border-radius: 0 !important; }
        .prelims-bill-table .ant-table-thead > tr > th { background-color: ${COLORS.tableHeaderBg} !important; }
        /* Explicit :hover so every row visibly highlights on hover, same fix
           as FoulSewersBillSummary.tsx/StormSewersBillSummary.tsx — plain CSS
           :hover applies regardless of any legacy colSpan render props a cell
           might use, unlike antd's own JS-driven hover class. */
        .prelims-bill-table .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
        .prelims-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td { background-color: #e0e7ff !important; }
        .prelims-bill-table .ant-table-tbody > tr.bg-gray-50:hover > td { background-color: #f3f4f6 !important; }
      `}</style>
      {hasData ? (
        <Table
          className="prelims-bill-table"
          dataSource={processedData}
          columns={columns}
          rowKey="key"
          bordered
          size="middle"
          expandable={{
            childrenColumnName: 'children',
            defaultExpandAllRows: false,
            expandedRowKeys: [...expandedRowKeys, ...groupRowKeys],
            expandIcon: ({ expanded, onExpand, record }) => {
              if (record.rowType === 'group' || !record.children?.length) return null;
              return (
                <button
                  type="button"
                  className={`ant-table-row-expand-icon ant-table-row-expand-icon-${expanded ? 'expanded' : 'collapsed'}`}
                  onClick={(e) => onExpand(record, e)}
                  aria-label={expanded ? 'Collapse row' : 'Expand row'}
                />
              );
            },
            onExpand: (expanded, record) => {
              setExpandedRowKeys(
                expanded
                  ? [...expandedRowKeys, record.key]
                  : expandedRowKeys.filter((k) => k !== record.key),
              );
            },
          }}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (record.rowType === 'sub-header' || record.rowType === 'main-header') classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === 'group' || record.rowType === 'column-type-header') classes.push('bg-gray-50', 'font-semibold');
            if (record.key === newlyAddedRowId) classes.push('prelims-summary-row-added');
            return classes.join(' ');
          }}
          pagination={false}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL PRELIMINARY ITEMS</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              <Table.Summary.Cell index={5} align="right">
                <span style={{ display: 'block', textAlign: 'right', fontWeight: 700 }}>
                  {fmtCurrency(grandTotal)}
                </span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={6} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode ? 7 : 6} />}
            </Table.Summary.Row>
          )}
        />
      ) : (
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <Text type="secondary">No preliminary items entered. Enter items on the Preliminary Items page.</Text>
        </div>
      )}
      <style>{`
        .prelims-bill-table .ant-table-thead > tr > th { min-width: 110px; }
        /* Newly-added extra Section/Item row (see newlyAddedRowId) — a brief
           highlight fade so the row is easy to spot the moment its parent
           auto-expands to reveal it. */
        @keyframes prelims-summary-row-added-fade {
          0% { background-color: #fef3c7; }
          100% { background-color: transparent; }
        }
        .prelims-summary-row-added > td {
          animation: prelims-summary-row-added-fade 1.8s ease-out;
        }
      `}</style>
      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => {
              const libItem = resourceCodeMap.get(code);
              return libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
            }}
          />
        )}
      </Modal>

      <Modal
        title="SummaryKey — Live Value (not yet in BOQ export)"
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const bc = computeDynamicBcCalculation(extraSummaryKeyRecord, (code) => {
            const libItem = resourceCodeMap.get(code);
            return libItem ? (libItem.nettRate || libItem.baseRate || 0) : 0;
          });
          return (
            <>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Description">{extraSummaryKeyRecord.description}</Descriptions.Item>
                {extraSummaryKeyRecord.quantity != null && (
                  <Descriptions.Item label="Quantity">{extraSummaryKeyRecord.quantity}</Descriptions.Item>
                )}
                {extraSummaryKeyRecord.unit && (
                  <Descriptions.Item label="Unit">{formatUnit(extraSummaryKeyRecord.unit)}</Descriptions.Item>
                )}
                {extraSummaryKeyRecord.rate != null && (
                  <Descriptions.Item label="Rate">£{extraSummaryKeyRecord.rate.toFixed(2)}</Descriptions.Item>
                )}
                {extraSummaryKeyRecord.resourceCode && (
                  <Descriptions.Item label="Resource Code">{extraSummaryKeyRecord.resourceCode}</Descriptions.Item>
                )}
                <Descriptions.Item label="Amount">{fmtCurrency(extraSummaryKeyRecord.amount)}</Descriptions.Item>
              </Descriptions>
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => (
                      <div key={idx}>
                        <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
    </div>
  );
};

export default PrelimsBillSummary;
